/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { HttpParams } from '@angular/common/http';
import { Params, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { Component, inject } from '@angular/core';
import { AbstractObject } from '@src/app/model/abstract-object';
import { ServiceRequest } from '@src/app/model/serviceRequest';
import { MONTHLY_TYPE, PERIOD_TYPES, WEEKLY_TYPE } from '@src/app/modules/crm/request-analysis/request-analysis.component';
import { PeriodType, periodName, priorPeriod, recentPeriods } from '@src/app/modules/crm/request-analysis/analysis-period';
import {
    OTHER_USERS, RequestAnalysisByUser, RequestAnalysisByUserService
} from '@src/app/modules/crm/request-analysis-by-user/request-analysis-by-user.service';
import { DailyTimeDialogComponent, DailyTimeDialogData } from '@src/app/modules/crm/request-analysis-by-user/daily-time-dialog.component';
import { ActionPageNavRoute } from '@src/app/routes/action-page-nav';
import { RequestAnalysisByUserPageNavRoute } from '@src/app/routes/request-analysis-by-user-page-nav';
import { ServiceRequestPageNavRoute } from '@src/app/routes/service-request-page-nav';
import { ButtonField } from '@src/app/shared/field/ButtonField';
import { Field } from '@src/app/shared/field/Field';
import { FieldMaker } from '@src/app/shared/field/FieldMaker';
import { NavRoute } from '@src/app/shared/NavRoute';
import { AbstractPageComponent } from '@src/app/shared/form/abstract-page.component';
import { FieldSet, LAYOUT_OPTIONS } from '@src/app/shared/form/field-set/field-set.component';
import { FormButtonComponent } from '@src/app/shared/form/form-button/form-button.component';
import { FormDurationComponent } from '@src/app/shared/form/form-duration/form-duration.component';
import { FormNavLinkComponent } from '@src/app/shared/form/form-nav-button/form-nav-link.component';
import { FormPageComponent } from '@src/app/shared/form/form-page/form-page.component';
import { FormPicklistComponent } from '@src/app/shared/form/form-picklist/form-picklist.component';
import { FormTextComponent } from '@src/app/shared/form/form-text/form-text.component';
import { FormConfig } from '@src/app/shared/form/FormConfig';

type MeasureOf = (o: RequestAnalysisByUser) => number;

const isOtherUsers = (o: RequestAnalysisByUser) => o?.userId === OTHER_USERS;

/** Keyed by Math.sign(current - previous) */
const TREND_ICONS = { [1]: 'north', [-1]: 'south', [0]: 'remove' };
/* Shrinks the standard 48px icon button so the trend column sits tight against its figure */
const COMPACT_ICON = { width: '24px', height: '24px', padding: '0' };
const TREND_STYLES = {
    [1]: { ...COMPACT_ICON, color: 'green' },
    [-1]: { ...COMPACT_ICON, color: 'red' },
    [0]: { ...COMPACT_ICON, color: 'gray' },
};

@Component({
    selector: 'app-request-analysis-by-user',
    templateUrl: './request-analysis-by-user.component.html',
    imports: [FormPageComponent]
})
export class RequestAnalysisByUserComponent extends AbstractPageComponent {
    dataSvc = inject(RequestAnalysisByUserService);
    private dialog = inject(MatDialog);
    private router = inject(Router);

    periodTypeField = FormPicklistComponent.make('Period Type', 'periodType', null,
        { items: PERIOD_TYPES }, { visible: Field.noShow });

    periodField = FormPicklistComponent.make('Period', 'period', null, { items: [], allowSelectNone: false });

    userField = FormPicklistComponent.make('User', 'userId', null, { items: [] }, { visible: Field.noShow });

    breakdownField = FormPicklistComponent.make('User', 'breakdownId', null, { items: [] },
        { visible: Field.noShow, sendServer: false });

    byOmcLink = FormNavLinkComponent.make('By OMC', RequestAnalysisByUserPageNavRoute,
        (o: RequestAnalysisByUser) => ({ periodType: o.periodType, period: o.period, userId: o.userId }),
        {
            name: 'byOmcLink',
            calculateValue: (o: RequestAnalysisByUser) => isOtherUsers(o) ? '' : $localize`By OMC`,
            btnOpts: { navRouteCalculator: (o: RequestAnalysisByUser) =>
                isOtherUsers(o) ? null : RequestAnalysisByUserPageNavRoute.getListRouteName() },
        });

    userNameField = FormTextComponent.make('User', 'userName', {
        calculateValue: (o: RequestAnalysisByUser) => isOtherUsers(o) ? $localize`Other users` : o.userName });
    fullNameField = FormTextComponent.make('Contact', 'fullName');
    emailField = FormTextComponent.make('Email', 'email');
    contactTypeField = FormTextComponent.make('Contact Type', 'contactType');
    omcNameField = FormTextComponent.make('OMC', 'omcName', { visible: Field.noShow });

    /* Hidden, only for the time chart - the visible time columns are links */
    timeSpentField = FormDurationComponent.make('Time Spent', 'timeSpent', { visible: Field.noShow, readonly: true });

    private previousPeriodName = '';

    config = new FormConfig({
        navRoute: RequestAnalysisByUserPageNavRoute,
        docPath: 'omc-professional/requests/request-analysis-by-user',
        title: $localize`Analysis by User`,
        help: $localize`Who is doing the work - backlog, and work closed this period and last, per user`,
        fieldSet: new FieldSet({
            fields: [
                this.periodTypeField,
                this.periodField,
                this.userField,
                this.breakdownField,
                this.userNameField,
                this.fullNameField,
                this.emailField,
                this.contactTypeField,
                this.omcNameField,
                this.byOmcLink,
                ...this.makeBacklogFigures(),
                ...this.makeClosedFigures(),
                this.timeSpentField,
                FieldMaker.spacer(),
            ],
            formLayout: LAYOUT_OPTIONS.singleCol,
        }),
        service: this.dataSvc,
        mode: 'list',
        beforeList: this.beforeList.bind(this),
    });

    private makeBacklogFigures(): Field[] {
        const openRequests = (o: RequestAnalysisByUser) =>
            ({ ...this.ownedBy(o), srStatus: ServiceRequest.srOpen.id });
        const openActions = (o: RequestAnalysisByUser) => ({ ...this.ownedBy(o), done: 0 });
        return [
            this.makeFigureLink($localize`Open Requests`, 'backlogRequests', ServiceRequestPageNavRoute, openRequests),
            this.makeFigureLink($localize`Open Actions`, 'backlogActions', ActionPageNavRoute, openActions),
            FormTextComponent.make($localize`Open Action Time`, 'backlogTime', {
                calculateValue: (o: RequestAnalysisByUser) => this.formatMinutes(o.backlogTime),
                cellOpts: { style: 'text-align: right' },
            }),
        ];
    }

    private makeClosedFigures(): ButtonField[] {
        const closedRequests = (o: RequestAnalysisByUser) => this.closedRequests(o);
        const closedActions = (o: RequestAnalysisByUser) => this.closedActions(o);
        const headingAlsoOverTrend: Partial<ButtonField> = { cellOpts:{ style: 'padding-right: 6px', linkAlign: 'right', thColspan: 2 } };
        return [
            this.makeFigureLink($localize`Requests Closed`, 'requestsClosed', ServiceRequestPageNavRoute,
                closedRequests, headingAlsoOverTrend),
            this.makeTrendIndicator('requestsClosed', (o) => o.requestsClosed, (o) => o.prevRequestsClosed),
            this.makeFigureLink($localize`Actions Closed`, 'actionsClosed', ActionPageNavRoute,
                closedActions, headingAlsoOverTrend),
            this.makeTrendIndicator('actionsClosed', (o) => o.actionsClosed, (o) => o.prevActionsClosed),
            this.makeTimeSpentFigure(closedActions, headingAlsoOverTrend),
            this.makeTrendIndicator('timeSpent', (o) => o.timeSpent, (o) => o.prevTimeSpent,
                (minutes) => this.formatMinutes(minutes)),
        ];
    }

    /** The combined row for users outside the agency has no list to link to, so shows plain figures */
    private makeFigureLink(label: string, measure: string, nav: NavRoute,
        listFilter: (o: RequestAnalysisByUser) => Params, options: Partial<ButtonField> = {}): ButtonField {
        return FormNavLinkComponent.make(label, nav, listFilter, {
            value: measure,
            cellOpts: { linkAlign: 'right' },
            btnOpts: { navRouteCalculator: (o: RequestAnalysisByUser) => isOtherUsers(o) ? null : nav.getListRouteName() },
            ...options,
        });
    }

    /** Weekly time opens the day-by-day breakdown; a month has no useful daily view, so links to the list */
    private makeTimeSpentFigure(listFilter: (o: RequestAnalysisByUser) => Params,
        options: Partial<ButtonField>): ButtonField {
        const actionsListUrl = (o: RequestAnalysisByUser) =>
            this.router.serializeUrl(this.router.createUrlTree([ActionPageNavRoute.getListRouteName(), listFilter(o)]));
        return FormButtonComponent.makeTextButton($localize`Time Spent`, 'timeSpentLink', () => null, {
            calculateValue: (o: RequestAnalysisByUser) => this.formatMinutes(o.timeSpent),
            btnOpts: {
                tableClickMethod: (o: RequestAnalysisByUser) => this.openTimeSpent(o, listFilter(o)),
                tableClickLinksTo: actionsListUrl,
            },
            ...options,
        });
    }

    private openTimeSpent(o: RequestAnalysisByUser, actionsListParams: Params) {
        if (isOtherUsers(o)) {
            return;
        }
        if (o.periodType !== WEEKLY_TYPE.id) {
            this.router.navigate([ActionPageNavRoute.getListRouteName(), actionsListParams]);
            return;
        }
        const data: DailyTimeDialogData = { row: o, userName: o.fullName || o.userName, actionsListParams };
        this.dialog.open(DailyTimeDialogComponent, { data });
    }

    private makeTrendIndicator(measure: string, current: MeasureOf, previous: MeasureOf,
        format: (value: number) => string = String): ButtonField {
        const trend = (o: RequestAnalysisByUser) => Math.sign(current(o) - previous(o));
        return FormButtonComponent.make('', measure + 'Trend', {
            type: 'icon', label: '',
            calculateValue: (o: RequestAnalysisByUser) => TREND_ICONS[trend(o)],
            btnOpts: { tableToolTip: (o: RequestAnalysisByUser) => this.describeTrend(current(o), previous(o), format) },
        }).setTableIconStyle((o: RequestAnalysisByUser) => TREND_STYLES[trend(o)])
            .override({ cellOpts: { thStyle: 'display:none', style: 'padding-left: 0px; padding-right: 4px', width: '24px' } });
    }

    /** e.g. "Up 25% on the previous period (Week 39: 22-28 Sep 2026), which had 8" */
    private describeTrend(current: number, previous: number, format: (value: number) => string) {
        const previousPeriod = $localize`the previous period (${this.previousPeriodName}), which had ${format(previous)}`;
        if (current === previous) {
            return $localize`Same as ${previousPeriod}`;
        }
        const direction = current > previous ? $localize`Up` : $localize`Down`;
        if (previous === 0) {
            return $localize`${direction} on ${previousPeriod}`;
        }
        const percent = Math.round(Math.abs(current - previous) / previous * 100);
        return $localize`${direction} ${percent}% on ${previousPeriod}`;
    }

    private formatMinutes(minutes: number) {
        return this.timeSpentField.formatValue(minutes) || '0';
    }

    private ownedBy(o: RequestAnalysisByUser): Params {
        return { ownedById: o.userId, ...this.inOmc(o) };
    }

    private closedRequests(o: RequestAnalysisByUser): Params {
        return { resolvedById: o.userId, [this.periodType(o).closeField]: o.period, ...this.inOmc(o) };
    }

    private closedActions(o: RequestAnalysisByUser): Params {
        return { completedById: o.userId, [this.periodType(o).workField]: o.period, ...this.inOmc(o) };
    }

    /** Both the request and action lists filter an OMC by its team */
    private inOmc(o: RequestAnalysisByUser): Params {
        return o.omcTeamId ? { teamId: o.omcTeamId } : {};
    }

    private periodType(o: RequestAnalysisByUser) {
        return o.periodType === MONTHLY_TYPE.id ? MONTHLY_TYPE : WEEKLY_TYPE;
    }

    beforeList(data: AbstractObject[], parms: HttpParams) {
        const byOmc = parms.has('userId');
        const periodType = parms.get('periodType') as PeriodType;
        const period = parms.get('period');
        const rows = data.map(row => Object.assign(new RequestAnalysisByUser(), row, { periodType, period }));
        this.previousPeriodName = periodName(priorPeriod(period));

        this.showBreakdownColumns(byOmc);
        this.periodField.setPicklistItems(recentPeriods(periodType, periodType === 'week' ? 52 : 24));

        const breakdowns = rows.map(row => ({ id: row.breakdownId, name: this.breakdownName(row, byOmc) }));
        this.breakdownField.setPicklistItems(breakdowns);
        if (!byOmc) {
            this.userField.setPicklistItems(breakdowns.filter(b => b.id !== OTHER_USERS));
        }
        return rows;
    }

    private breakdownName(row: RequestAnalysisByUser, byOmc: boolean) {
        if (byOmc) {
            return row.omcName;
        }
        return isOtherUsers(row) ? $localize`Other users` : (row.fullName || row.userName);
    }

    private showBreakdownColumns(byOmc: boolean) {
        const userColumn = byOmc ? Field.noShow : Field.showAll;
        for (const field of [this.userNameField, this.fullNameField, this.emailField,
            this.contactTypeField, this.byOmcLink]) {
            field.visible = userColumn;
        }
        this.omcNameField.visible = byOmc ? Field.showAll : Field.noShow;
        this.breakdownField.label = byOmc ? $localize`OMC` : $localize`User`;
    }
}
